import { globalBaseConfig, getEnvironmentIdentifier } from "@maersk-global/shared-js";

export enum Environment {
  LOCAL = "local",
  DEV = "dev",
  PREPROD = "preprod",
  PROD = "prod"
}

const PROTOCOL = globalBaseConfig.DEFAULT_PROTOCOL;
const SUBDOMAIN = "";
const DOMAIN = "maersk.com";

/** Shared SSI / window.envVars bindings for deployed hosts (dev, preprod, prod). */
function readDeployedAppEnvFromWindow(options: { includeRequestUrlNew?: boolean } = {}) {
  const envVars = globalThis.envVars;
  return {
    requestURL: envVars?.VITE_REQUEST_URL,
    ...(options.includeRequestUrlNew ? { requestURLNew: envVars?.VITE_REQUEST_URL_NEW } : {}),
    reportingUrl: envVars?.VITE_REPORTING_URL,
    mcpClientChatURL: envVars?.VITE_MCP_CLIENT_CHAT_URL,
    lfd2_0FeatureFlag: envVars?.VITE_LFD2_0_FEATURE_FLAG,
    showLandingCatalogueTilesFeatureFlag: envVars?.VITE_SHOW_LANDING_CATALOGUE_TILES,
    showLegalTeamsFeatureFlag: envVars?.VITE_SHOW_LEGAL_TEAMS,
    showServiceCatalogueManagerV1FeatureFlag: envVars?.VITE_SHOW_SERVICE_CATALOGUE_MANAGER_V1,
    showServiceCatalogueLandingFeatureFlag: envVars?.VITE_SHOW_SERVICE_CATALOGUE_LANDING,
    maintenanceActive: envVars?.VITE_MAINTENANCE_ACTIVE,
    environmentBanner: envVars?.VITE_ENVIRONMENT_BANNER,
    sharepointDocsSiteId: envVars?.VITE_SHAREPOINT_DOCS_SITE_ID,
    sharepointDocsListId: envVars?.VITE_SHAREPOINT_DOCS_LIST_ID,
    sharepointDocsCategoryField: envVars?.VITE_SHAREPOINT_DOCS_CATEGORY_FIELD,
    sharepointDocsSubCategoryField: envVars?.VITE_SHAREPOINT_DOCS_SUBCATEGORY_FIELD,
    sharepointDocsServiceActivityField: envVars?.VITE_SHAREPOINT_DOCS_SERVICE_ACTIVITY_FIELD,
    graphListItemsPreferNonindexed: envVars?.VITE_GRAPH_LIST_ITEMS_PREFER_NONINDEXED,
    regionalSetupURL: envVars?.VITE_REGIONAL_SETUP_URL,
    copilotStudioDirectConnectUrl: envVars?.VITE_COPILOT_STUDIO_DIRECT_CONNECT_URL,
    copilotSsoScope: envVars?.VITE_COPILOT_SSO_SCOPE,
    copilotStudioFeatureFlag: envVars?.VITE_COPILOT_STUDIO_FEATURE_FLAG
  };
}

function readRumEnvFromWindow(rumAppEnv: Environment.PREPROD | Environment.PROD) {
  const envVars = globalThis.envVars;
  return {
    rumApiToken: envVars?.VITE_RUM_API_TOKEN,
    rumAppName: envVars?.VITE_RUM_APP_NAME,
    rumAppEnv
  };
}

const ENVIRONMENTS = {
  localhost: {
    name: Environment.LOCAL,
    webDomain: "http://localhost:5001",
    apiDomain: "https://api-cdt.maersk.com/legalfrontdoorapp-requestregisterservice",
    resolutionServiceApiDomain: "https://api-cdt.maersk.com/legalfrontdoorapp-requestresolverservice",
    adminServiceApiDomain: "https://api-cdt.maersk.com/legalfrontdoorapp-adminservice",
    maerskHome: "https://teamsite.maerskgroup.com/sites/OneMaersk",
    reportingUrl: "https://legalfrontdoorapp-reportingservicepp-cdt.maersk.io/",
    authRedirectUri: "http://localhost:5001/",
    appClientId: "6963e887-ddd7-4f67-8238-f370dcb3ae27",
    appAuthority: "https://login.microsoftonline.com/05d75c05-fa1a-42e7-9cf1-eb416c396f2d",
    authLogoutRedirectUri: "https://teamsite.maerskgroup.com/sites/cf-gle",
    scopes: ["6963e887-ddd7-4f67-8238-f370dcb3ae27/.default"],
    appScope: ["https://graph.microsoft.com/.default"],
    copilotChannelScopes: ["https://api.powerplatform.com/.default"],
    requestURL: "request-details/{id}?fromComponent=emailNotification",
    mcpClientChatURL: "https://api-cdt.maersk.com/legalfrontdoorapp-mcpclient/chat",
    lfd2_0FeatureFlag: true,
    showLandingCatalogueTilesFeatureFlag: true,
    showLegalTeamsFeatureFlag: false,
    showServiceCatalogueManagerV1FeatureFlag: true,
    showServiceCatalogueLandingFeatureFlag: true,
    maintenanceActive: false,
    environmentBanner: true,
    sharepointDocsSiteId:
      "teamsite.maerskgroup.com,9a8dce93-2466-4868-bb3f-98d01aa52b63,f004f87c-ac77-4887-9820-69dda0deef29",
    sharepointDocsListId: "2e8b08f1-0296-4d63-b7a0-2d7c4c55c191",
    sharepointDocsCategoryField: "Category1",
    sharepointDocsSubCategoryField: "SubCategory",
    sharepointDocsServiceActivityField: "ServiceActivity",
    graphListItemsPreferNonindexed: "",
    regionalSetupURL:
      "https://teamsite.maerskgroup.com/sites/cf-gle/SitePages/Regional-country-setup-in-Legal-for-LFD.aspx",
    copilotStudioDirectConnectUrl:
      "https://119c1bd4cdfce427a1df4abdd40af1.54.environment.api.powerplatform.com/copilotstudio/dataverse-backed/authenticated/bots/cr2ef_AskLegal/conversations?api-version=2022-03-01-preview",
    copilotSsoScope: "api://855d6647-59c1-4588-bbc0-2436ef0ac907/askLegal.scope",
    copilotStudioFeatureFlag: true
  },
  dev: {
    name: Environment.DEV,
    webDomain: `${PROTOCOL}dev.${SUBDOMAIN}.${DOMAIN}`,
    apiDomain: globalThis.envVars?.VITE_API_DOMAIN,
    resolutionServiceApiDomain: globalThis.envVars?.VITE_RESOLUTION_SERVICE_API_DOMAIN,
    adminServiceApiDomain: globalThis.envVars?.VITE_ADMIN_SERVICE_API_DOMAIN,
    appClientId: globalThis.envVars?.VITE_APP_CLIENT_ID,
    appAuthority: globalThis.envVars?.VITE_APP_AUTHORITY,
    maerskHome: globalThis.envVars?.VITE_MAERSK_HOME,
    authRedirectUri: globalThis.envVars?.VITE_AUTH_REDIRECT_URI,
    authLogoutRedirectUri: globalThis.envVars?.VITE_AUTH_LOGOUT_REDIRECT_URI,
    scopes: ["6963e887-ddd7-4f67-8238-f370dcb3ae27/.default"],
    appScope: ["https://graph.microsoft.com/.default"],
    copilotChannelScopes: ["https://api.powerplatform.com/.default"],
    ...readDeployedAppEnvFromWindow()
  },
  preprod: {
    name: Environment.PREPROD,
    webDomain: `${PROTOCOL}dev.${SUBDOMAIN}.${DOMAIN}`,
    apiDomain: globalThis.envVars?.VITE_API_DOMAIN,
    resolutionServiceApiDomain: globalThis.envVars?.VITE_RESOLUTION_SERVICE_API_DOMAIN,
    adminServiceApiDomain: globalThis.envVars?.VITE_ADMIN_SERVICE_API_DOMAIN,
    appClientId: globalThis.envVars?.VITE_APP_CLIENT_ID,
    appAuthority: globalThis.envVars?.VITE_APP_AUTHORITY,
    maerskHome: globalThis.envVars?.VITE_MAERSK_HOME,
    authRedirectUri: globalThis.envVars?.VITE_AUTH_REDIRECT_URI,
    authLogoutRedirectUri: globalThis.envVars?.VITE_AUTH_LOGOUT_REDIRECT_URI,
    scopes: ["02b500b8-b9fb-47e4-add8-3f1314a9368d/.default"],
    appScope: ["https://graph.microsoft.com/.default"],
    copilotChannelScopes: ["https://api.powerplatform.com/.default"],
    ...readRumEnvFromWindow(Environment.PREPROD),
    ...readDeployedAppEnvFromWindow({ includeRequestUrlNew: true })
  },
  prod: {
    name: Environment.PROD,
    webDomain: `${PROTOCOL}${SUBDOMAIN}.${DOMAIN}`,
    apiDomain: globalThis.envVars?.VITE_API_DOMAIN,
    resolutionServiceApiDomain: globalThis.envVars?.VITE_RESOLUTION_SERVICE_API_DOMAIN,
    adminServiceApiDomain: globalThis.envVars?.VITE_ADMIN_SERVICE_API_DOMAIN,
    appClientId: globalThis.envVars?.VITE_APP_CLIENT_ID,
    appAuthority: globalThis.envVars?.VITE_APP_AUTHORITY,
    maerskHome: globalThis.envVars?.VITE_MAERSK_HOME,
    authRedirectUri: globalThis.envVars?.VITE_AUTH_REDIRECT_URI,
    authLogoutRedirectUri: globalThis.envVars?.VITE_AUTH_LOGOUT_REDIRECT_URI,
    scopes: ["6d25eba0-357a-4fae-9ed1-a4fa96744362/.default"],
    appScope: ["https://graph.microsoft.com/.default"],
    copilotChannelScopes: ["https://api.powerplatform.com/.default"],
    ...readRumEnvFromWindow(Environment.PROD),
    ...readDeployedAppEnvFromWindow()
  }
};

const environmentsMap = new Map<string, any>([
  ["localhost", ENVIRONMENTS.localhost],
  ["localhost:5001", ENVIRONMENTS.localhost],
  ["dev", ENVIRONMENTS.dev],
  ["cdt", ENVIRONMENTS.preprod],
  ["preprod", ENVIRONMENTS.preprod],
  ["prod", ENVIRONMENTS.prod]
]);

export function getEnvConfig() {
  const envIdentifier = String(getEnvironmentIdentifier()).toLowerCase();

  // shared-js returns "cdt" for both dev and preprod CDT hosts; disambiguate by hostname
  if (envIdentifier === "cdt") {
    const hostname = globalThis.location?.hostname?.toLowerCase();
    if (hostname?.includes("preprod")) {
      return ENVIRONMENTS.preprod;
    }
    return ENVIRONMENTS.dev;
  }

  const envConfig = environmentsMap.get(envIdentifier);
  console.debug("loaded env:", envConfig);
  if (envConfig) {
    return envConfig;
  }

  return environmentsMap.get("prod");
}
